// Іконки застосунку в стилі iOS (SF Symbols): градієнтні заокруглені плитки
// з білими stroke-гліфами замість емодзі. Використовуються в ModeGrid, TabBar,
// Home і Premium — щоб інтерфейс виглядав як сучасний нативний додаток.

type Kind =
  | 'exam'
  | 'topics'
  | 'tickets'
  | 'hardest'
  | 'mistakes'
  | 'bookmarks'
  | 'signs'
  | 'battle'
  | 'gift'
  | 'trophy'
  | 'crown'
  | 'home'
  | 'tests'
  | 'profile'
  | 'bell'
  | 'flash'
  | 'target'
  | 'friends'
  | 'medal'
  | 'channel'
  | 'support'
  | 'admin'
  | 'noads';

// Градієнти плиток — палітра iOS (світлий → насичений)
const TILE_GRADIENTS: Record<Kind, [string, string]> = {
  exam: ['#3ddc6a', '#1fa045'],
  topics: ['#3ea6ff', '#0a68d6'],
  tickets: ['#ffd60a', '#f0a500'],
  hardest: ['#ff5c7a', '#d91a3f'],
  mistakes: ['#ffa552', '#e86b00'],
  bookmarks: ['#31d0c2', '#0a9e92'],
  signs: ['#7a78f0', '#4a48c4'],
  battle: ['#c46cf5', '#8e2de2'],
  gift: ['#ff6482', '#e0325a'],
  trophy: ['#ffcf3f', '#e89b00'],
  crown: ['#ffd60a', '#f0a500'],
  home: ['#3ea6ff', '#0a68d6'],
  tests: ['#3ddc6a', '#1fa045'],
  profile: ['#9ba6b5', '#6b7686'],
  bell: ['#ff5c7a', '#d91a3f'],
  flash: ['#ffd60a', '#f0a500'],
  target: ['#ff5c7a', '#d91a3f'],
  friends: ['#3ea6ff', '#0a68d6'],
  medal: ['#ffcf3f', '#e89b00'],
  channel: ['#3ea6ff', '#0a68d6'],
  support: ['#3ddc6a', '#1fa045'],
  admin: ['#9ba6b5', '#6b7686'],
  noads: ['#ff5c7a', '#d91a3f'],
};

// Білі stroke-гліфи (viewBox 24×24, stroke 1.8, округлі кінці — як SF Symbols)
function Glyph({ kind }: { kind: Kind }) {
  const common = {
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.8,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  };
  switch (kind) {
    case 'exam': // аркуш із галочкою
      return (
        <g {...common}>
          <path d="M7 3.5h7.5L18.5 7v13a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4.5a1 1 0 0 1 1-1Z" />
          <path d="M14.5 3.5V7H18" />
          <path d="M9.2 14.2l2 2 3.6-4" />
        </g>
      );
    case 'topics': // розгорнута книга
      return (
        <g {...common}>
          <path d="M12 6.2C10.5 4.9 8.4 4.3 5.5 4.3c-.6 0-1 .4-1 1v12.2c0 .6.4 1 1 1 2.9 0 5 .6 6.5 1.9 1.5-1.3 3.6-1.9 6.5-1.9.6 0 1-.4 1-1V5.3c0-.6-.4-1-1-1-2.9 0-5 .6-6.5 1.9Z" />
          <path d="M12 6.2v14.2" />
        </g>
      );
    case 'tickets': // квиток
      return (
        <g {...common}>
          <path d="M4 9.5V7a1 1 0 0 1 1-1h14a1 1 0 0 1 1 1v2.5a2.5 2.5 0 0 0 0 5V17a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-2.5a2.5 2.5 0 0 0 0-5Z" />
          <path d="M14 6v2.2M14 11v2M14 15.8V18" strokeDasharray="0.1 3.4" />
        </g>
      );
    case 'hardest': // полум'я
      return (
        <g {...common}>
          <path d="M12 3.5c.6 2.8-.6 4.4-2 5.9-1.5 1.6-2.8 3-2.8 5.4A4.9 4.9 0 0 0 12 19.7a4.9 4.9 0 0 0 4.8-4.9c0-3.4-2.4-4.6-2.9-7.1-1 .8-1.5 1.9-1.4 3.3-1-.9-1.2-4.4-.5-7.5Z" />
        </g>
      );
    case 'mistakes': // стрілки-повтор
      return (
        <g {...common}>
          <path d="M5 9a7.5 7.5 0 0 1 13-1.5" />
          <path d="M18.5 4v3.8h-3.8" />
          <path d="M19 15a7.5 7.5 0 0 1-13 1.5" />
          <path d="M5.5 20v-3.8h3.8" />
        </g>
      );
    case 'bookmarks': // серце
      return (
        <g {...common}>
          <path d="M12 19.5s-6.8-4.1-8.4-8A4.6 4.6 0 0 1 12 7.6a4.6 4.6 0 0 1 8.4 3.8c-1.6 4-8.4 8.1-8.4 8.1Z" />
        </g>
      );
    case 'signs': // світлофор
      return (
        <g {...common}>
          <rect x="8.5" y="3.5" width="7" height="17" rx="2.4" />
          <circle cx="12" cy="7.4" r="1.15" fill="currentColor" stroke="none" />
          <circle cx="12" cy="12" r="1.15" fill="currentColor" stroke="none" />
          <circle cx="12" cy="16.6" r="1.15" fill="currentColor" stroke="none" />
        </g>
      );
    case 'battle': // блискавка
      return (
        <g {...common}>
          <path d="M13.2 3 6.5 13.2h4.3L10.8 21l6.7-10.2h-4.3L13.2 3Z" />
        </g>
      );
    case 'gift': // подарунок
      return (
        <g {...common}>
          <rect x="4.5" y="9" width="15" height="11" rx="1.2" />
          <path d="M4.5 12.8h15M12 9v11" />
          <path d="M12 9c-4.2 0-5-1.6-5-2.7C7 5.2 8 4.3 9.2 4.3 11 4.3 12 6.5 12 9Zm0 0c4.2 0 5-1.6 5-2.7 0-1.1-1-2-2.2-2C13 4.3 12 6.5 12 9Z" />
        </g>
      );
    case 'trophy': // кубок
      return (
        <g {...common}>
          <path d="M8 4.5h8v5a4 4 0 0 1-8 0v-5Z" />
          <path d="M8 6H5.2c0 3 1.2 4.6 2.9 5M16 6h2.8c0 3-1.2 4.6-2.9 5" />
          <path d="M12 13.5v3M9 19.5h6M10 16.5h4" />
        </g>
      );
    case 'crown': // корона
      return (
        <g {...common}>
          <path d="M4.5 8.5 8 11l4-5 4 5 3.5-2.5-1.3 9a1 1 0 0 1-1 .9H6.8a1 1 0 0 1-1-.9l-1.3-9Z" />
        </g>
      );
    case 'home': // будинок
      return (
        <g {...common}>
          <path d="M4.5 11.5 12 4.5l7.5 7" />
          <path d="M6.5 10v9a1 1 0 0 0 1 1h9a1 1 0 0 0 1-1v-9" />
          <path d="M10 20v-5.5h4V20" />
        </g>
      );
    case 'tests': // стос карток
      return (
        <g {...common}>
          <rect x="5" y="7.5" width="14" height="12" rx="1.6" />
          <path d="M7.5 4.8h9M9 12h6M9 15h4" />
        </g>
      );
    case 'profile': // людина
      return (
        <g {...common}>
          <circle cx="12" cy="8.2" r="3.4" />
          <path d="M5 20a7.3 7.3 0 0 1 14 0" />
        </g>
      );
    case 'bell': // дзвіночок
      return (
        <g {...common}>
          <path d="M12 4a5.4 5.4 0 0 0-5.4 5.4c0 4.7-1.6 6-1.6 6h14s-1.6-1.3-1.6-6A5.4 5.4 0 0 0 12 4Z" />
          <path d="M10.3 18.5a1.8 1.8 0 0 0 3.4 0" />
        </g>
      );
    case 'flash': // блискавка у колі (швидкість)
      return (
        <g {...common}>
          <circle cx="12" cy="12" r="8.2" />
          <path d="M12.8 7.5 9.5 12.6h2.6l-.9 3.9 3.3-5.1h-2.6l.9-3.9Z" />
        </g>
      );
    case 'target': // мішень
      return (
        <g {...common}>
          <circle cx="12" cy="12" r="8.2" />
          <circle cx="12" cy="12" r="4.6" />
          <circle cx="12" cy="12" r="1.2" fill="currentColor" stroke="none" />
        </g>
      );
    case 'friends': // двоє людей
      return (
        <g {...common}>
          <circle cx="9" cy="8.5" r="2.9" />
          <path d="M3.8 19a5.6 5.6 0 0 1 10.4 0" />
          <path d="M15.5 6.3a2.9 2.9 0 0 1 0 4.9" />
          <path d="M16.6 13.9a5.6 5.6 0 0 1 3.6 5.1" />
        </g>
      );
    case 'medal': // медаль зі стрічкою
      return (
        <g {...common}>
          <circle cx="12" cy="14.5" r="4.7" />
          <path d="m12 12.6.9 1.8 2 .3-1.5 1.4.4 2-1.8-1-1.8 1 .4-2-1.5-1.4 2-.3.9-1.8Z" strokeWidth="1.2" />
          <path d="M8.7 10.6 6.5 4h4l1.5 4.4L13.5 4h4l-2.2 6.6" />
        </g>
      );
    case 'channel': // рупор
      return (
        <g {...common}>
          <path d="M18.5 5.5v13l-6.5-3.4H6.5a2 2 0 0 1-2-2v-2.2a2 2 0 0 1 2-2H12l6.5-3.4Z" />
          <path d="M8.5 15.5V19a1 1 0 0 0 1 1h1.2" />
        </g>
      );
    case 'support': // бульбашка чату
      return (
        <g {...common}>
          <path d="M12 4.5c-4.4 0-8 3-8 6.8 0 2 .9 3.7 2.5 4.9L6 20l3.5-1.5c.8.2 1.6.3 2.5.3 4.4 0 8-3 8-6.8s-3.6-6.5-8-6.5Z" />
          <path d="M8.8 11.3h.01M12 11.3h.01M15.2 11.3h.01" strokeWidth="2.4" />
        </g>
      );
    case 'admin': // шестерня
      return (
        <g {...common}>
          <circle cx="12" cy="12" r="2.6" />
          <path d="M12 3.8v2.1M12 18.1v2.1M20.2 12h-2.1M5.9 12H3.8M17.8 6.2l-1.5 1.5M7.7 16.3l-1.5 1.5M17.8 17.8l-1.5-1.5M7.7 7.7 6.2 6.2" />
        </g>
      );
    case 'noads': // перекреслене коло (без реклами)
      return (
        <g {...common}>
          <circle cx="12" cy="12" r="8.2" />
          <path d="M6.2 6.2l11.6 11.6" />
        </g>
      );
  }
}

/** Градієнтна iOS-плитка з гліфом — для карток режимів, списків і бейджів. */
export function IconTile({
  kind,
  className = 'h-11 w-11',
  glyphClass = 'h-6 w-6',
  dimmed = false,
}: {
  kind: Kind;
  className?: string;
  glyphClass?: string;
  dimmed?: boolean;
}) {
  const [from, to] = TILE_GRADIENTS[kind];
  return (
    <div
      className={`grid shrink-0 place-items-center rounded-xl text-white shadow-sm ${className} ${
        dimmed ? 'opacity-60 saturate-50' : ''
      }`}
      style={{ background: `linear-gradient(180deg, ${from}, ${to})` }}
    >
      <svg viewBox="0 0 24 24" className={glyphClass} aria-hidden="true">
        <Glyph kind={kind} />
      </svg>
    </div>
  );
}

/** Плаский stroke-гліф без плитки — для TabBar (колір успадковує від тексту). */
export function TabGlyph({ kind, className = 'h-6 w-6' }: { kind: Kind; className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      <Glyph kind={kind} />
    </svg>
  );
}

/** Маленький замок Premium — замість емодзі 🔒. */
export function LockBadge({ className = '' }: { className?: string }) {
  return (
    <span
      className={`grid h-6 w-6 place-items-center rounded-full bg-black/25 text-yellow-400 ${className}`}
      aria-label="Premium"
    >
      <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
        <rect x="6" y="10.5" width="12" height="9" rx="1.8" />
        <path d="M8.5 10.5V8a3.5 3.5 0 0 1 7 0v2.5" />
      </svg>
    </span>
  );
}
